/* =========================================================
   Southern Made sections – shared behaviour
   <sm-carousel>  scroll-snap carousel: arrows, dots, autoplay
   <sm-accordion> details groups (FAQ) – optional single open
   <sm-footer>    footer menus collapse on mobile only
   ========================================================= */
(function () {
  var MOBILE = window.matchMedia('(max-width: 767px)');

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ---------------- Carousel ---------------- */
  if (!customElements.get('sm-carousel')) {
    class SmCarousel extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        onReady(this.setup.bind(this));
      }

      disconnectedCallback() {
        this.stopAutoplay();
        if (this._ro) this._ro.disconnect();
      }

      setup() {
        this.track = this.querySelector('.SmCarousel__Track');
        if (!this.track) return;
        this.slides = Array.prototype.slice.call(this.track.children);
        this.prevBtn = this.querySelector('.SmCarousel__Arrow--prev');
        this.nextBtn = this.querySelector('.SmCarousel__Arrow--next');
        this.dotsWrap = this.querySelector('.SmCarousel__Dots');
        this.loop = this.dataset.loop === 'true';
        this.autoplay = this.dataset.autoplay === 'true';
        this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.paused = false;

        if (this.prevBtn) this.prevBtn.addEventListener('click', this.go.bind(this, -1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.go.bind(this, 1));

        var update = this.update.bind(this);
        this.track.addEventListener('scroll', function () {
          window.requestAnimationFrame(update);
        }, { passive: true });

        var pause = this.setPaused.bind(this, true);
        var resume = this.setPaused.bind(this, false);
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.addEventListener('touchstart', pause, { passive: true });

        this._ro = new ResizeObserver(this.refresh.bind(this));
        this._ro.observe(this.track);

        var self = this;
        document.addEventListener('shopify:block:select', function (e) {
          var index = self.slides.indexOf(e.target.closest('.SmCarousel__Slide'));
          if (index > -1) {
            self.setPaused(true);
            self.scrollToIndex(index, false);
          }
        });
        document.addEventListener('shopify:block:deselect', function () {
          self.setPaused(false);
        });

        this.refresh();
      }

      isScrollable() {
        var display = window.getComputedStyle(this.track).display;
        return display === 'flex' && this.track.scrollWidth > this.track.clientWidth + 2;
      }

      step() {
        if (this.slides.length > 1) {
          return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        }
        return this.track.clientWidth;
      }

      perView() {
        var step = this.step();
        if (!step) return 1;
        return Math.max(1, Math.round((this.track.clientWidth + 1) / step));
      }

      refresh() {
        if (!this.track) return;
        var active = this.isScrollable();
        this.classList.toggle('is-active', active);
        this.buildDots(active);
        this.update();
        if (active && this.autoplay && !window.Shopify?.designMode) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      }

      buildDots(active) {
        if (!this.dotsWrap) return;
        this.dotsWrap.innerHTML = '';
        if (!active) return;
        var per = this.perView();
        var pages = Math.ceil(this.slides.length / per);
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'SmCarousel__Dot';
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener('click', this.scrollToIndex.bind(this, i * per, true));
          this.dotsWrap.appendChild(dot);
        }
      }

      maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      update() {
        if (!this.track) return;
        var left = this.track.scrollLeft;
        var max = this.maxScroll();
        var atStart = left <= 2;
        var atEnd = left >= max - 2;

        if (this.prevBtn) this.prevBtn.disabled = !this.loop && atStart;
        if (this.nextBtn) this.nextBtn.disabled = !this.loop && atEnd;

        if (!this.dotsWrap) return;
        var dots = this.dotsWrap.children;
        if (!dots.length) return;
        var page = Math.round(left / (this.step() * this.perView()));
        if (atEnd) page = dots.length - 1;
        page = Math.min(Math.max(page, 0), dots.length - 1);
        for (var i = 0; i < dots.length; i++) {
          dots[i].classList.toggle('is-active', i === page);
          dots[i].setAttribute('aria-current', i === page ? 'true' : 'false');
        }
      }

      scrollToIndex(index, smooth) {
        var slide = this.slides[Math.min(Math.max(index, 0), this.slides.length - 1)];
        if (!slide) return;
        this.track.scrollTo({
          left: Math.min(slide.offsetLeft - this.slides[0].offsetLeft, this.maxScroll()),
          behavior: smooth === false ? 'auto' : 'smooth'
        });
      }

      go(direction) {
        var max = this.maxScroll();
        var left = this.track.scrollLeft;
        var distance = this.step() * this.perView();
        if (direction > 0 && left >= max - 2) {
          if (this.loop) this.track.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }
        if (direction < 0 && left <= 2) {
          if (this.loop) this.track.scrollTo({ left: max, behavior: 'smooth' });
          return;
        }
        this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      }

      setPaused(value) {
        this.paused = value;
      }

      startAutoplay() {
        this.stopAutoplay();
        var self = this;
        this._timer = window.setInterval(function () {
          if (self.paused || document.hidden) return;
          var max = self.maxScroll();
          if (self.track.scrollLeft >= max - 2) {
            self.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            self.track.scrollBy({ left: self.step() * self.perView(), behavior: 'smooth' });
          }
        }, this.speed);
      }

      stopAutoplay() {
        if (this._timer) window.clearInterval(this._timer);
        this._timer = null;
      }
    }
    customElements.define('sm-carousel', SmCarousel);
  }

  /* ---------------- Accordion (FAQ) ---------------- */
  if (!customElements.get('sm-accordion')) {
    class SmAccordion extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var self = this;
        this.addEventListener('toggle', function (e) {
          var item = e.target;
          if (self.dataset.single !== 'true' || !item.open) return;
          self.querySelectorAll('details[open]').forEach(function (d) {
            if (d !== item) d.open = false;
          });
        }, true);
        document.addEventListener('shopify:block:select', function (e) {
          if (self.contains(e.target) && e.target.tagName === 'DETAILS') e.target.open = true;
        });
      }
    }
    customElements.define('sm-accordion', SmAccordion);
  }

  /* ---------------- Footer (accordion on mobile) ---------------- */
  if (!customElements.get('sm-footer')) {
    class SmFooter extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        onReady(this.setup.bind(this));
      }

      setup() {
        this.items = Array.prototype.slice.call(this.querySelectorAll('.SmFooter__Col'));
        this.accordion = this.dataset.accordion === 'true';
        var self = this;

        this.items.forEach(function (item) {
          var summary = item.querySelector('summary');
          if (!summary) return;
          summary.addEventListener('click', function (e) {
            if (!self.accordion || !MOBILE.matches) {
              e.preventDefault();
              return;
            }
            if (self.dataset.single === 'true' && !item.open) {
              self.items.forEach(function (other) {
                if (other !== item) other.open = false;
              });
            }
          });
        });

        this.sync = this.sync.bind(this);
        if (MOBILE.addEventListener) {
          MOBILE.addEventListener('change', this.sync);
        } else {
          MOBILE.addListener(this.sync);
        }
        this.sync();
      }

      sync() {
        var collapse = this.accordion && MOBILE.matches;
        this.items.forEach(function (item) {
          item.open = !collapse;
        });
        this.classList.add('is-ready');
      }
    }
    customElements.define('sm-footer', SmFooter);
  }
})();
